import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";
import { useLang, useSettings, API } from "../lib/providers";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { firePixelEvent } from "./TrackingScripts";

export default function Reseller() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [mode, setMode] = useState("intro"); // intro | form | sent
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    structure_name: "",
    city: "",
    type: "club",
    products_wanted: "",
    estimated_qty: "",
    purchase_frequency: "",
    phone: "",
    email: "",
  });

  if (settings.reseller_enabled === false) return null;

  const title = lang === "ar" ? settings.reseller_title_ar : settings.reseller_title_fr;
  const text = lang === "ar" ? settings.reseller_text_ar : settings.reseller_text_fr;
  const cta = lang === "ar" ? settings.reseller_cta_ar : settings.reseller_cta_fr;

  const submit = async () => {
    if (!form.structure_name.trim() || !form.phone.trim()) {
      toast.error(lang === "ar" ? "يرجى ملء الاسم والهاتف" : "Nom de la structure et téléphone obligatoires");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/b2b/leads`, form);
      // Fire Lead event (strong intent signal for Meta/TikTok audience building)
      firePixelEvent("Lead", {
        content_name: "B2B reseller inquiry",
        content_category: form.structure_type || "reseller",
      });
      toast.success(lang === "ar" ? "تم إرسال طلبكم" : "Demande envoyée ! Je vous contacte sous 24h.");
      setMode("sent");
    } catch {
      toast.error(lang === "ar" ? "خطأ" : "Erreur, réessayez");
    } finally { setBusy(false); }
  };

  return (
    <section id="b2b" className="py-16 sm:py-20 theme-section-alt scroll-mt-24" data-testid="reseller-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-neon/30 bg-neon/5 p-6 sm:p-10">
          {/* Big bold B2B title */}
          <div className="text-center mb-6">
            <p className="font-heading font-black tracking-tighter text-6xl sm:text-7xl lg:text-8xl text-neon leading-none select-none" data-testid="b2b-tag">
              B2B
            </p>
            <h2 className="mt-4 font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl" data-testid="reseller-title">{title}</h2>
            {text && <p className="text-white/75 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mt-4" data-testid="reseller-text">{text}</p>}
          </div>

          {mode === "intro" && (
            <div className="flex justify-center">
              <Button
                onClick={() => setMode("form")}
                className="bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest px-6 py-5 text-sm h-auto"
                data-testid="reseller-open-form-btn"
                style={{ textShadow: "none" }}
              >
                <MessageCircle className="w-4 h-4 me-2" />
                {cta}
              </Button>
            </div>
          )}

          {mode === "form" && (
            <div className="space-y-3 max-w-2xl mx-auto mt-4" data-testid="reseller-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder={lang === "ar" ? "اسم المؤسسة *" : "Nom de la structure *"} value={form.structure_name} onChange={(e) => setForm({ ...form, structure_name: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-structure" />
                <Input placeholder={lang === "ar" ? "المدينة / الولاية" : "Ville / Wilaya"} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-city" />
              </div>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="bg-black/40 border-white/15 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--c-bg)] border-white/15 text-white">
                  <SelectItem value="club">{lang === "ar" ? "نادي" : "Club"}</SelectItem>
                  <SelectItem value="coach">{lang === "ar" ? "مدرب" : "Coach"}</SelectItem>
                  <SelectItem value="reseller">{lang === "ar" ? "موزع" : "Revendeur"}</SelectItem>
                </SelectContent>
              </Select>
              <Textarea placeholder={lang === "ar" ? "المنتجات المطلوبة (ماركات، فئات)" : "Produits recherchés (marques, catégories)"} value={form.products_wanted} onChange={(e) => setForm({ ...form, products_wanted: e.target.value })} rows={2} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-products" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder={lang === "ar" ? "الكميات التقديرية" : "Quantités estimées"} value={form.estimated_qty} onChange={(e) => setForm({ ...form, estimated_qty: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-qty" />
                <Input placeholder={lang === "ar" ? "تواتر الشراء" : "Fréquence d'achat"} value={form.purchase_frequency} onChange={(e) => setForm({ ...form, purchase_frequency: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-frequency" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder={lang === "ar" ? "الهاتف *" : "Téléphone *"} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-phone" />
                <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/40 border-white/15 rounded-none" data-testid="b2b-email" />
              </div>
              <Button onClick={submit} disabled={busy} className="w-full bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest py-5 h-auto" data-testid="b2b-submit">
                <Send className="w-4 h-4 me-2" />
                {busy ? "…" : (lang === "ar" ? "أرسل الطلب" : "Envoyer ma demande")}
              </Button>
              <button type="button" onClick={() => setMode("intro")} className="text-xs text-white/40 hover:text-white underline w-full text-center">
                {lang === "ar" ? "إلغاء" : "Annuler"}
              </button>
            </div>
          )}

          {mode === "sent" && (
            <div className="text-center py-8 space-y-3" data-testid="reseller-sent">
              <p className="text-5xl">✅</p>
              <p className="font-heading font-black uppercase tracking-tight text-xl">
                {lang === "ar" ? "شكراً !" : "Merci !"}
              </p>
              <p className="text-white/70 text-sm max-w-md mx-auto">
                {lang === "ar" ? "تم استلام طلبكم. سأتواصل معكم قريباً مع شروط سعر احترافي." : "Votre demande est bien reçue. Je vous contacte sous 24h avec les conditions tarifaires pro."}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
